import { memo, useMemo, type ReactNode } from 'react'
import { Platform, View, type StyleProp, type ViewProps, type ViewStyle } from 'react-native'
import { GlassView, isLiquidGlassAvailable } from 'expo-glass-effect'
import { LinearGradient } from 'expo-linear-gradient'
import { useReducedMotion } from 'react-native-reanimated'
import { COLORS } from '@/lib/brand'
import { useReducedTransparency } from '@/lib/hooks/use-reduced-transparency'
import { useTheme } from '@/lib/hooks/use-theme'
import { RADIUS, useSemanticColors, withAlpha } from '@/lib/theme/tokens'
import { webShadow } from '@/lib/theme/web-shadow'

type GlassTone = 'regular' | 'quiet' | 'tint'
type GlassVisualRole = 'surface' | 'chrome' | 'sheet'

type GlassSurfaceProps = {
  children: ReactNode
  /** Regular is readable UI, quiet is supporting chrome, tint carries a brand cue. */
  tone?: GlassTone
  radius?: number
  /** Layout for the content inset; keeps the material itself free of padding on Android. */
  contentStyle?: StyleProp<ViewStyle>
  style?: StyleProp<ViewStyle>
  testID?: string
  pointerEvents?: ViewProps['pointerEvents']
  accessibilityRole?: ViewProps['accessibilityRole']
  accessibilityLabel?: ViewProps['accessibilityLabel']
  accessibilityState?: ViewProps['accessibilityState']
  accessibilityViewIsModal?: ViewProps['accessibilityViewIsModal']
  importantForAccessibility?: ViewProps['importantForAccessibility']
  /** A sparse brand tint. Never use as a substitute for a semantic status color. */
  tintColor?: string
  /**
   * `surface` is intentionally static on web/Android for scrolling density.
   * `chrome` and `sheet` are small, persistent planes that can afford real
   * browser backdrop filtering; iOS gets native Liquid Glass in every role.
   */
  visualRole?: GlassVisualRole
  /** Only interactive native Liquid Glass controls should opt into this. */
  interactive?: boolean
}

/**
 * Shared Liquid Glass material for functional chrome and 2.0 content surfaces.
 *
 * Native iOS 26 receives Apple's real Liquid Glass through `GlassView`.
 * Android and older iOS receive the exact same semantic hierarchy through a
 * static composite: translucent base, illuminated edge and soft depth. It is not
 * a fake blur — blur over Mapbox and long lists compromises frame time on the
 * Redmi — but it reads as one coherent material across every platform.
 *
 * Use it for controls floating over rich content (map chrome, persistent nav),
 * never to turn ordinary content cards into decoration.
 */
export const GlassSurface = memo(function GlassSurface({
  children,
  tone = 'regular',
  radius = RADIUS.lg,
  contentStyle,
  style,
  testID,
  pointerEvents,
  accessibilityRole,
  accessibilityLabel,
  accessibilityState,
  accessibilityViewIsModal,
  importantForAccessibility,
  tintColor,
  visualRole = 'surface',
  interactive = false,
}: GlassSurfaceProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const reducedTransparency = useReducedTransparency()
  const reducedMotion = useReducedMotion() === true
  const regular = tone === 'regular'
  const tinted = tone === 'tint'
  const resolvedTint = tintColor ?? (tinted ? COLORS.purple.light : undefined)
  // Keep enough of the ambient canvas visible that a group of surfaces reads
  // as material rather than a stack of opaque white cards. The semantic
  // contrast comes from type, border and elevation—not an almost-solid fill.
  // Android deliberately has no live backdrop blur: it is too expensive over
  // Mapbox, the keyboard and long feeds. A bottom sheet therefore needs a
  // denser frost than the browser/iOS version or the content beneath it reads
  // through the form fields. It remains a material via its illuminated edge,
  // tint and depth, while text and controls keep their own visual plane.
  const staticAndroidSheet = visualRole === 'sheet' && Platform.OS === 'android'
  const glassBackgroundOpacity =
    visualRole === 'sheet'
      ? regular
        ? isDark
          ? staticAndroidSheet
            ? 0.9
            : 0.82
          : staticAndroidSheet
            ? 0.9
            : 0.76
        : isDark
          ? staticAndroidSheet
            ? 0.86
            : 0.7
          : staticAndroidSheet
            ? 0.88
            : 0.62
      : visualRole === 'chrome'
        ? regular
          ? isDark
            ? 0.92
            : 0.9
          : isDark
            ? 0.86
            : 0.82
        : regular
          ? isDark
            ? 0.7
            : 0.56
          : isDark
            ? 0.56
            : 0.44
  const glassBorderOpacity = regular ? (isDark ? 0.22 : 0.78) : isDark ? 0.16 : 0.62
  const borderOpacity = reducedTransparency ? (isDark ? 0.32 : 1) : glassBorderOpacity
  const useNativeLiquidGlass =
    Platform.OS === 'ios' && !reducedTransparency && isLiquidGlassAvailable()
  const fallbackFillColor = withAlpha(resolvedTint ?? colors.surface, glassBackgroundOpacity)
  // A static specular highlight gives Android's no-blur fallback the same
  // directional material cue as the native glass without creating a live
  // backdrop layer. It is deliberately limited to small chrome and sheets,
  // never repeated across feed cards while scrolling.
  const rendersStaticSpecular =
    Platform.OS === 'android' && !reducedTransparency && visualRole !== 'surface'
  // A transparent elevated Fabric node can composite its child content into a
  // separate rectangular hardware layer on some Android GPUs. The bright
  // border supplies the edge; skip elevation there rather than trading visual
  // polish for a broken material. iOS and web retain their native depth.
  const materialStyle = useMemo<ViewStyle>(() => {
    const surfaceShadow =
      Platform.OS === 'android' && !useNativeLiquidGlass
        ? {}
        : webShadow({
            y: visualRole === 'sheet' ? -4 : regular ? 6 : 3,
            blur: visualRole === 'sheet' ? 24 : regular ? 18 : 12,
            opacity: isDark ? 0.24 : 0.13,
            elevation: visualRole === 'sheet' ? 6 : regular ? 5 : 3,
          })
    // Browser blur is intentionally limited to small floating planes. Putting
    // backdrop-filter on every feed card turns a long scroll into dozens of
    // offscreen compositing layers; native iOS still receives real Liquid
    // Glass for every supported surface.
    const webMaterial =
      Platform.OS === 'web' && !reducedTransparency && visualRole !== 'surface'
        ? ({
            backdropFilter: `blur(${visualRole === 'sheet' ? 24 : 18}px) saturate(160%)`,
            WebkitBackdropFilter: `blur(${visualRole === 'sheet' ? 24 : 18}px) saturate(160%)`,
          } as unknown as ViewStyle)
        : {}

    return {
      overflow: 'hidden',
      borderRadius: radius,
      // iOS 26 owns its optical treatment. Painting our fallback white layer
      // over `GlassView` would technically render the native component while
      // visually hiding the thing we asked it to do.
      backgroundColor: useNativeLiquidGlass
        ? 'transparent'
        : reducedTransparency
          ? colors.surface
          : 'transparent',
      borderWidth: useNativeLiquidGlass ? 0 : 1,
      borderColor: useNativeLiquidGlass ? 'transparent' : withAlpha('#FFFFFF', borderOpacity),
      // A light-facing upper edge makes the Android fallback feel dimensional
      // without a blur or an overlay layer. Unlike an absolute gradient child,
      // it is not affected by the consuming surface's padding.
      borderTopColor: useNativeLiquidGlass
        ? 'transparent'
        : withAlpha('#FFFFFF', reducedTransparency ? 0.86 : isDark ? 0.3 : 0.96),
      borderBottomColor: useNativeLiquidGlass
        ? 'transparent'
        : withAlpha('#FFFFFF', reducedTransparency ? 0.86 : isDark ? 0.16 : 0.46),
      ...webMaterial,
      ...surfaceShadow,
    }
  }, [
    borderOpacity,
    colors.surface,
    isDark,
    radius,
    reducedTransparency,
    regular,
    useNativeLiquidGlass,
    visualRole,
  ])

  const glassEffectStyle = useMemo(
    () => ({
      style: regular ? ('regular' as const) : ('clear' as const),
      animate: !reducedMotion,
      animationDuration: 0.28,
    }),
    [reducedMotion, regular],
  )

  // `expo-glass-effect` intentionally falls back to a plain View off iOS,
  // but rendering that compatibility component with iOS-only props has shown
  // padding-bound compositing artifacts on some Android GPU stacks. Branching
  // here is intentional: the Android material is an ordinary, predictable
  // View; only a device that can truly render Liquid Glass receives GlassView.
  if (!useNativeLiquidGlass) {
    return (
      <View
        testID={testID}
        pointerEvents={pointerEvents}
        accessibilityRole={accessibilityRole}
        accessibilityLabel={accessibilityLabel}
        accessibilityState={accessibilityState}
        accessibilityViewIsModal={accessibilityViewIsModal}
        importantForAccessibility={importantForAccessibility}
        style={[materialStyle, style]}
      >
        {reducedTransparency ? null : (
          // In Fabric on Android, a translucent background attached to a
          // padded rounded View can be rasterised only inside its content box
          // on a few GPU drivers. `contentStyle` keeps padding in a child;
          // this unpadded outer layer owns the material and rounded clip.
          <View
            pointerEvents="none"
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              borderRadius: radius,
              backgroundColor: fallbackFillColor,
            }}
          />
        )}
        {rendersStaticSpecular ? (
          <LinearGradient
            pointerEvents="none"
            colors={
              isDark
                ? [
                    withAlpha('#FFFFFF', 0.08),
                    withAlpha(COLORS.purple.light, 0.025),
                    withAlpha(COLORS.coral.DEFAULT, 0.06),
                  ]
                : [
                    withAlpha('#FFFFFF', 0.24),
                    withAlpha('#FFFFFF', 0),
                    withAlpha(COLORS.purple.light, 0.08),
                  ]
            }
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              borderRadius: radius,
            }}
          />
        ) : null}
        {contentStyle ? <View style={[{ flexGrow: 1 }, contentStyle]}>{children}</View> : children}
      </View>
    )
  }

  return (
    <GlassView
      testID={testID}
      pointerEvents={pointerEvents}
      accessibilityRole={accessibilityRole}
      accessibilityLabel={accessibilityLabel}
      accessibilityState={accessibilityState}
      accessibilityViewIsModal={accessibilityViewIsModal}
      importantForAccessibility={importantForAccessibility}
      colorScheme={isDark ? 'dark' : 'light'}
      glassEffectStyle={glassEffectStyle}
      tintColor={resolvedTint}
      isInteractive={interactive}
      style={[materialStyle, style]}
    >
      {contentStyle ? <View style={[{ flexGrow: 1 }, contentStyle]}>{children}</View> : children}
    </GlassView>
  )
})
